import React, { useEffect, useState, useRef } from 'react';
import { Typography } from 'antd';
import type { TextWidgetParams } from '@/types/chatbot.types';
import * as S from './widgets.styles';

const { Paragraph } = Typography;

const TextWidget: React.FC<TextWidgetParams> = ({
  content = '',
  onAnimationComplete,
  shouldStart = true,
  isStreaming = false,
}) => {
  const [displayedText, setDisplayedText] = useState('');
  const [isAnimating, setIsAnimating] = useState(false);
  const [shouldAnimate, setShouldAnimate] = useState(false); // Start with false, will be set based on logic
  const contentRef = useRef<string>('');
  const animationIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const hasInitializedRef = useRef<boolean>(false);
  const onCompleteRef = useRef(onAnimationComplete);

  // Update callback ref when it changes
  useEffect(() => {
    onCompleteRef.current = onAnimationComplete;
  }, [onAnimationComplete]);

  useEffect(() => {
    if (!content || !shouldStart) {
      setDisplayedText(content || '');
      setIsAnimating(false);
      setShouldAnimate(false);
      if (!shouldStart) {
        contentRef.current = '';
        hasInitializedRef.current = false;
      }
      return;
    }

    const previousContent = contentRef.current;
    const isNewContent = content !== previousContent;
    const isNewMessage = previousContent === '' || !hasInitializedRef.current;

    if (isNewContent) {
      // Clear any existing animation
      if (animationIntervalRef.current) {
        clearInterval(animationIntervalRef.current);
        animationIntervalRef.current = null;
      }

      contentRef.current = content;
      hasInitializedRef.current = true;

      // Clean animation logic:
      // 1. Never animate during streaming (chunks already provide progressive reveal)
      // 2. Only animate complete text if it's long enough (> 50 chars)
      // 3. Show immediately otherwise
      const shouldAnimateText = !isStreaming && isNewMessage && content.length > 50;

      if (shouldAnimateText) {
        // Animate long complete text
        setDisplayedText('');
        setShouldAnimate(true);
        setIsAnimating(true);

        let currentIndex = 0;
        const targetContent = content;
        animationIntervalRef.current = setInterval(() => {
          if (currentIndex < targetContent.length) {
            setDisplayedText(targetContent.substring(0, currentIndex + 1));
            currentIndex++;
          } else {
            if (animationIntervalRef.current) {
              clearInterval(animationIntervalRef.current);
              animationIntervalRef.current = null;
            }
            setIsAnimating(false);
            setShouldAnimate(false);
            // Call completion callback
            if (onCompleteRef.current) {
              onCompleteRef.current();
            }
          }
        }, 5); // 5ms per character for fast typing effect
      } else {
        // Show content immediately (streaming or short text)
        setDisplayedText(content);
        setIsAnimating(false);
        setShouldAnimate(false);
        // Call completion immediately
        if (onCompleteRef.current) {
          onCompleteRef.current();
        }
      }
    }

    return () => {
      if (animationIntervalRef.current) {
        clearInterval(animationIntervalRef.current);
        animationIntervalRef.current = null;
      }
    };
  }, [content, shouldStart, isStreaming]);

  const formatContent = (text: string) => {
    // Basic markdown-like formatting
    const lines = text.split('\n');
    const formatted: JSX.Element[] = [];

    lines.forEach((line, index) => {
      // Bold text
      const boldFormatted = line.split('**').map((part, i) => {
        if (i % 2 === 1) {
          return <strong key={`bold-${index}-${i}`}>{part}</strong>;
        }
        return part;
      });

      formatted.push(
        <React.Fragment key={`line-${index}`}>
          {boldFormatted}
          {index < lines.length - 1 && <br />}
        </React.Fragment>,
      );
    });

    return formatted;
  };

  return (
    <S.TextWidgetContainer>
      <Paragraph style={{ whiteSpace: 'pre-wrap', margin: 0 }}>
        {formatContent(shouldAnimate ? displayedText : content)}
        {isAnimating && displayedText.length < content.length && <S.TypingCursor />}
      </Paragraph>
    </S.TextWidgetContainer>
  );
};

export default TextWidget;
